import React, { useState } from 'react';
import { Card } from '../ui/card.js';
import { useSecuritySettings } from '../../stores/useSettingsStore.js';
import { Shield, Lock, Clock, Globe, Key, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

const SecurityTab: React.FC = () => {
  const { security, setSecurity } = useSecuritySettings();
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleMfaToggle = async () => {
    setLoading(true);
    try {
      setSecurity({ mfaEnabled: !security.mfaEnabled });
      toast.success(`Two-factor authentication ${!security.mfaEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating MFA settings:', error);
      toast.error('Failed to update two-factor authentication');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionTimeoutChange = async (timeout: number) => {
    try {
      setSecurity({ sessionTimeout: timeout });
      toast.success('Session timeout updated');
    } catch (error) {
      console.error('Error updating session timeout:', error);
      toast.error('Failed to update session timeout');
    }
  };

  const handleAllowedOriginsChange = (origins: string) => {
    const originsArray = origins.split(',').map(origin => origin.trim()).filter(Boolean);
    setSecurity({ allowedOrigins: originsArray });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Security Settings</h3>
        <p className="text-sm text-slate-400 mb-6">
          Manage your account security, authentication, and access controls.
        </p>
      </div>

      {/* Password Management */}
      <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="w-5 h-5 text-red-400" />
          <h4 className="text-base font-medium cream-gradient-text text-drop-shadow">Password</h4>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
              placeholder="Enter current password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
              placeholder="Enter new password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
              placeholder="Confirm new password"
            />
          </div>

          <button className="violett-button text-drop-shadow w-full p-3 text-sm font-medium">
            Update Password
          </button>
        </div>
      </Card>

      {/* Two-Factor Authentication */}
      <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-5 h-5 text-green-400" />
          <h4 className="text-base font-medium cream-gradient-text text-drop-shadow">Two-Factor Authentication</h4>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-300">Enable 2FA</p>
            <p className="text-xs text-slate-500">
              Add an extra layer of security to your account
            </p>
          </div>
          <button
            onClick={handleMfaToggle}
            disabled={loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              security.mfaEnabled ? 'bg-green-600' : 'bg-slate-600'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                security.mfaEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {security.mfaEnabled && (
          <div className="mt-4 p-4 bg-slate-700 rounded-lg">
            <p className="text-sm text-green-400 mb-2">✓ Two-factor authentication is enabled</p>
            <button className="violett-button text-drop-shadow text-sm px-3 py-1">
              View Recovery Codes
            </button>
          </div>
        )}
      </Card>

      {/* Session Management */}
      <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Clock className="w-5 h-5 text-blue-400" />
          <h4 className="text-base font-medium cream-gradient-text text-drop-shadow">Session Management</h4>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Session Timeout (minutes)
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="5"
                max="120"
                value={security.sessionTimeout}
                onChange={(e) => handleSessionTimeoutChange(parseInt(e.target.value))}
                className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <span className="text-sm text-slate-400 w-12 text-right">
                {security.sessionTimeout}m
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Automatically log out after this period of inactivity
            </p>
          </div>

          <div className="p-4 bg-slate-700 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-300">Active Sessions</span>
              <span className="text-xs text-slate-500">3 sessions</span>
            </div>
            <button className="violett-button text-drop-shadow text-sm px-3 py-1">
              Terminate All Other Sessions
            </button>
          </div>
        </div>
      </Card>

      {/* API Access Control */}
      <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Globe className="w-5 h-5 text-purple-400" />
          <h4 className="text-base font-medium cream-gradient-text text-drop-shadow">API Access Control</h4>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Allowed Origins
            </label>
            <textarea
              value={security.allowedOrigins.join(', ')}
              onChange={(e) => handleAllowedOriginsChange(e.target.value)}
              className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
              rows={3}
              placeholder="https://example.com, https://app.example.com"
            />
            <p className="text-xs text-slate-500 mt-1">
              Comma-separated list of allowed origins for API access
            </p>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-300">Show API Keys</span>
            <button
              onClick={() => setShowApiKeys(!showApiKeys)}
              className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors"
            >
              {showApiKeys ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {showApiKeys && (
            <div className="p-4 bg-slate-700 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-slate-300">API Key</span>
              </div>
              <code className="text-xs text-slate-400 font-mono break-all">
                bh_1234567890abcdef1234567890abcdef
              </code>
              <button className="violett-button text-drop-shadow mt-2 text-sm px-3 py-1">
                Regenerate API Key
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SecurityTab;
